import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IMock } from '../../interfaces/decision.interface';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DecideService {
  private apiUrl = 'https://api.example.com/decisions'; // Replace with your API endpoint

  constructor(private http: HttpClient) {}
  decisionsSubject = new BehaviorSubject<IMock | null>(null);

  mockData: IMock = {
    scenario: {
      title:
        'Implementing WorkSync – The New Time Tracking System at AstraZeneca',
      role_user: {
        id: '12',
        name: 'Tom Walker (YOU)',
        job_title: 'Director, SPQ - Change Management',
        level: 4,
        stage_name: 'Adoption',
        points: 50,
        image: '',
        childs: null,
      },
      context:
        'AstraZeneca: A global pharmaceutical company embarking on a major transformation project sponsored by Alexandra Bennett, SVP, Chief Digital Officer & CIO.\n\nThe company is replacing its outdated time tracking system with WorkSync, an AI-powered solution designed to: Improve payroll processes. Automate approvals. Deliver enhanced workforce analytics.',
      challenges: [
        'Employee Resistance: There is concern among employees about WorkSync being perceived as a micromanagement tool and potential changes to payroll and overtime policies.',
        'Managerial Concerns: Managers, including those in Business Technology Group (BTG) 1 and BTG 2, express worries about disruptions to workflow and the tangible benefits of WorkSync.',
        'Implementation Challenges: The integration with existing IT infrastructure and developing effective training programs present significant hurdles.',
        'High Expectations & Timeline: Alexandra Bennett has established a challenging 80-week deadline for full WorkSync implementation, adding stress due to concurrent initiatives and change fatigue.',
      ],
      mission:
        'Your mission is to influence employees and managers to view WorkSync as a beneficial enhancement rather than a burden, ensuring voluntary and effective adoption.\n\nImplement change management interventions, leveraging strategic communication and engagement plans to manage skepticism and resistance effectively.',
      final_message:
        'Lead AstraZeneca through this transformation to ensure WorkSync is fully embraced and successfully integrated before the deadline, ultimately improving operational efficiency and workforce insights.\n\nTo effectively promote the adoption of WorkSync, you must choose strategic actions that align with change management principles. In the next phase, you will explore a series of change management interventions guided by the ADKAR model. Each decision you make will influence the speed of adoption, levels of engagement, and the long-term success of the initiative.\n\nCan you lead AstraZeneca through this transformation and ensure WorkSync is fully embraced before the deadline? 🚀',
      credibility: 7,
      critical_mass: 1,
      total_weeks: 80,
    },
    stages: [
      {
        id: '1',
        name: 'Unawareness',
        score_limit: 6,
      },
      {
        id: '2',
        name: 'Awareness',
        score_limit: 20,
      },
      {
        id: '3',
        name: 'Interest',
        score_limit: 30,
      },
      {
        id: '4',
        name: 'Experimentation',
        score_limit: 40,
      },
      {
        id: '5',
        name: 'Adoption',
        score_limit: 50,
      },
    ],
    decisions: [
      {
        id: '1',
        name: 'Connect with Key Stakeholders and address initial concerns',
        scope: 3,
        description:
          'Connecting with key stakeholders ensures their concerns are addressed early, helping to establish support for the change initiative.',
        implementation_weeks: 4,
        weeks_until_reuse: 4,
      },
      {
        id: '2',
        name: 'Identify and activate change champions within the org',
        scope: 3,
        description:
          'Activating change champions within the organization is crucial to build a network of supporters who will advocate for the change.',
        implementation_weeks: 6,
        weeks_until_reuse: 6,
      },
      {
        id: '3',
        name: 'Design and deliver training sessions',
        scope: 20,
        description:
          'Designing and delivering training sessions equips employees with the necessary skills and knowledge to adapt to the change.',
        implementation_weeks: 8,
        weeks_until_reuse: 12,
      },
      {
        id: '4',
        name: 'Conduct a pilot project',
        scope: 3,
        description:
          'Conducting a pilot project allows for a small-scale test to identify potential challenges and fine-tune the implementation before full-scale rollout.',
        implementation_weeks: 4,
        weeks_until_reuse: 12,
      },
      {
        id: '5',
        name: 'Embed change into process and policies',
        scope: 20,
        description:
          'Embedding change into process and policies ensures that the change becomes a part of the organizational culture and long-term practices.',
        implementation_weeks: 12,
        weeks_until_reuse: 13,
      },
      {
        id: '6',
        name: 'Communicate the reason for change',
        scope: 20,
        description:
          'Communicating the reason for change helps to create understanding and alignment among employees, making the transition smoother.',
        implementation_weeks: 4,
        weeks_until_reuse: 10,
      },
      {
        id: '7',
        name: 'Obtain public support from the Sponsor',
        scope: 20,
        description:
          'Obtaining public support from the sponsor reinforces the legitimacy of the change and ensures it is backed by top leadership.',
        implementation_weeks: 2,
        weeks_until_reuse: 6,
      },
      {
        id: '8',
        name: 'Coach and mentor to reinforce training',
        scope: 20,
        description:
          'Coaching and mentoring helps reinforce training and provides ongoing support to employees, ensuring they continue to embrace the change.',
        implementation_weeks: 4,
        weeks_until_reuse: 12,
      },
      {
        id: '9',
        name: 'Address skill gaps within key stakeholders',
        scope: 1,
        description:
          'Addressing skill gaps within key stakeholders helps ensure that those in critical roles have the capabilities needed to support the change.',
        implementation_weeks: 6,
        weeks_until_reuse: 8,
      },
      {
        id: '10',
        name: 'Provide ongoing support',
        scope: 20,
        description:
          'Providing ongoing support ensures that employees have the resources and assistance they need to maintain the change over time.',
        implementation_weeks: 10,
        weeks_until_reuse: 10,
      },
      {
        id: '11',
        name: 'Obtain the support from a consultant',
        scope: 20,
        description:
          'Obtaining support from a consultant can provide expert guidance, ensuring the change is implemented successfully and effectively.',
        implementation_weeks: 8,
        weeks_until_reuse: 12,
      },
      {
        id: '12',
        name: 'Tell a success story',
        scope: 20,
        description:
          'Telling a success story demonstrates the positive outcomes of the change, motivating others to adopt it.',
        implementation_weeks: 1,
        weeks_until_reuse: 1,
      },
      {
        id: '13',
        name: 'Organize Q&A Meetings',
        scope: 20,
        description:
          'Organizing Q&A meetings allows employees to ask questions and clarify doubts, fostering transparency and trust during the change process.',
        implementation_weeks: 3,
        weeks_until_reuse: 6,
      },
      {
        id: '14',
        name: 'Include in shadowing / hands on sessions',
        scope: 3,
        description:
          'Including hands-on sessions or shadowing provides employees with practical experience and helps them learn the change through direct involvement.',
        implementation_weeks: 6,
        weeks_until_reuse: 8,
      },
      {
        id: '15',
        name: 'Give recognition to an adopter',
        scope: 1,
        description:
          'Recognizing adopters helps motivate others by showing appreciation for those who embrace the change and encouraging wider participation.',
        implementation_weeks: 3,
        weeks_until_reuse: 10,
      },
    ],
    organisation_chart: {
      id: '1',
      name: 'Alexandra Bennett',
      job_title: 'SVP, Chief Digital Officer & CIO - IT',
      level: 1,
      stage_name: 'Unawareness',
      points: 0,
      image: '',
      childs: [
        {
          id: '2',
          name: 'Alejandro Martinez',
          job_title: 'Executive Director IT - Strategy, Performance & Qualit',
          level: 2,
          stage_name: 'Unawareness',
          points: 0,
          image: '',
          childs: [
            {
              id: '5',
              name: 'Sarah Johnson',
              job_title: 'Senior Director, SPQ - Strategy, Change & Results',
              level: 3,
              stage_name: 'Unawareness',
              points: 0,
              image: '',
              childs: [
                {
                  id: '12',
                  name: 'Tom Walker (YOU)',
                  job_title: 'Director, SPQ - Change Management',
                  level: 4,
                  stage_name: 'Adoption',
                  points: 50,
                  image: '',
                  childs: null,
                },
                {
                  id: '15',
                  name: 'Anna Thompson',
                  job_title: 'Business Change Management',
                  level: 4,
                  stage_name: 'Unawareness',
                  points: 0,
                  image: '',
                  childs: null,
                },
              ],
            },
            {
              id: '6',
              name: 'Linda Garcia',
              job_title: 'Senior Director, SPQ - Quality Management',
              level: 3,
              stage_name: 'Unawareness',
              points: 0,
              image: '',
              childs: [
                {
                  id: '13',
                  name: 'Laura White',
                  job_title: 'Director, SPQ - IT Quality Management',
                  level: 4,
                  stage_name: 'Unawareness',
                  points: 0,
                  image: '',
                  childs: null,
                },
              ],
            },
            {
              id: '7',
              name: 'Jonathan Kim',
              job_title: 'Senior Director, SPQ - Portfolio Management',
              level: 3,
              stage_name: 'Unawareness',
              points: 0,
              image: '',
              childs: [
                {
                  id: '14',
                  name: 'Sophie Turner',
                  job_title: 'Director, SPQ - Portfolio Management Operations',
                  level: 4,
                  stage_name: 'Unawareness',
                  points: 0,
                  image: '',
                  childs: null,
                },
                {
                  id: '16',
                  name: 'Kevin Evans',
                  job_title: 'Business Performance Manager',
                  level: 4,
                  stage_name: 'Unawareness',
                  points: 0,
                  image: '',
                  childs: null,
                },
              ],
            },
          ],
        },
        {
          id: '3',
          name: 'Emma Woods',
          job_title: 'VP R&D IT',
          level: 2,
          stage_name: 'Unawareness',
          points: 0,
          image: '',
          childs: [
            {
              id: '8',
              name: 'Sophia Lee',
              job_title: 'Executive Director, R&D IT - Development & Late TA',
              level: 3,
              stage_name: 'Unawareness',
              points: 0,
              image: '',
              childs: null,
            },
            {
              id: '9',
              name: 'Ethan Green',
              job_title: 'Senior Director, R&D IT - AI Strategy & Innovation',
              level: 3,
              stage_name: 'Unawareness',
              points: 0,
              image: '',
              childs: [
                {
                  id: '17',
                  name: 'Mark Stevens',
                  job_title: 'AI Product Analyst',
                  level: 4,
                  stage_name: 'Unawareness',
                  points: 0,
                  image: '',
                  childs: null,
                },
                {
                  id: '18',
                  name: 'Rachel Adams',
                  job_title: 'Knowledge Graph Product Lead',
                  level: 4,
                  stage_name: 'Unawareness',
                  points: 0,
                  image: '',
                  childs: null,
                },
              ],
            },
          ],
        },
        {
          id: '4',
          name: 'Louise Orysh',
          job_title: 'VP, Enterprise Technology Services IT',
          level: 2,
          stage_name: 'Unawareness',
          points: 0,
          image: '',
          childs: [
            {
              id: '10',
              name: 'Thomas Becker',
              job_title: 'Senior Director, ETS - Global IT Risk & Compliances',
              level: 3,
              stage_name: 'Unawareness',
              points: 0,
              image: '',
              childs: [
                {
                  id: '19',
                  name: 'Emily Carter',
                  job_title:
                    'Director - Risk Business Partner & Risk Assurance',
                  level: 4,
                  stage_name: 'Unawareness',
                  points: 0,
                  image: '',
                  childs: null,
                },
              ],
            },
            {
              id: '11',
              name: 'Olivia Chen',
              job_title: 'Executive Director - Global Cyber Security',
              level: 3,
              stage_name: 'Unawareness',
              points: 0,
              image: '',
              childs: [
                {
                  id: '20',
                  name: 'Jack Thompson',
                  job_title: 'Director, Cyber Security - Business Operations',
                  level: 4,
                  stage_name: 'Unawareness',
                  points: 0,
                  image: '',
                  childs: null,
                },
              ],
            },
          ],
        },
      ],
    },
    organisation_stages: [
      {
        id: '1',
        stage_name: 'Mobilization',
        percentage_threshold: 40,
        user_stage: 'Awareness',
      },
      {
        id: '2',
        stage_name: 'Motion',
        percentage_threshold: 60,
        user_stage: 'Interest',
      },
      {
        id: '3',
        stage_name: 'Sustainability',
        percentage_threshold: 80,
        user_stage: 'Adoption',
      },
    ],
    user_list: [
      {
        id: '1',
        name: 'Alexandra Bennett',
        job_title: 'SVP, Chief Digital Officer & CIO - IT',
        level: 1,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '2',
        name: 'Alejandro Martinez',
        job_title: 'Executive Director IT - Strategy, Performance & Qualit',
        level: 2,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '3',
        name: 'Emma Woods',
        job_title: 'VP R&D IT',
        level: 2,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '4',
        name: 'Louise Orysh',
        job_title: 'VP, Enterprise Technology Services IT',
        level: 2,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '5',
        name: 'Sarah Johnson',
        job_title: 'Senior Director, SPQ - Strategy, Change & Results',
        level: 3,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '6',
        name: 'Linda Garcia',
        job_title: 'Senior Director, SPQ - Quality Management',
        level: 3,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '7',
        name: 'Jonathan Kim',
        job_title: 'Senior Director, SPQ - Portfolio Management',
        level: 3,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '8',
        name: 'Sophia Lee',
        job_title: 'Executive Director, R&D IT - Development & Late TA',
        level: 3,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '9',
        name: 'Ethan Green',
        job_title: 'Senior Director, R&D IT - AI Strategy & Innovation',
        level: 3,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '10',
        name: 'Thomas Becker',
        job_title: 'Senior Director, ETS - Global IT Risk & Compliances',
        level: 3,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '11',
        name: 'Olivia Chen',
        job_title: 'Executive Director - Global Cyber Security',
        level: 3,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '12',
        name: 'Tom Walker (YOU)',
        job_title: 'Director, SPQ - Change Management',
        level: 4,
        stage_name: 'Adoption',
        points: 50,
        image: '',
        childs: null,
      },
      {
        id: '13',
        name: 'Laura White',
        job_title: 'Director, SPQ - IT Quality Management',
        level: 4,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '14',
        name: 'Sophie Turner',
        job_title: 'Director, SPQ - Portfolio Management Operations',
        level: 4,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '15',
        name: 'Anna Thompson',
        job_title: 'Business Change Management',
        level: 4,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '16',
        name: 'Kevin Evans',
        job_title: 'Business Performance Manager',
        level: 4,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '17',
        name: 'Mark Stevens',
        job_title: 'AI Product Analyst',
        level: 4,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '18',
        name: 'Rachel Adams',
        job_title: 'Knowledge Graph Product Lead',
        level: 4,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '19',
        name: 'Emily Carter',
        job_title: 'Director - Risk Business Partner & Risk Assurance',
        level: 4,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
      {
        id: '20',
        name: 'Jack Thompson',
        job_title: 'Director, Cyber Security - Business Operations',
        level: 4,
        stage_name: 'Unawareness',
        points: 0,
        image: '',
        childs: null,
      },
    ],
  };
  // Method to perform a GET request
  getDecisions(): Promise<IMock> {
    return Promise.resolve(this.mockData); // Return only the decisions array as a resolved Promise
  }
}
